import { useState, useEffect, useRef, useCallback } from 'react';

import { FlexGrid, FlexGridColumn, FlexGridCellTemplate } from '@grapecity/wijmo.react.grid';

import { CellType, CellRange, KeyAction, DataMap, ClipStringOptions } from '@grapecity/wijmo.grid';
import { saveFile, Point, CollectionView, Clipboard } from '@grapecity/wijmo';
import SelectComponent from 'components/Input/SelectComponent';

import { Button, Card, Row, Col, Form } from 'react-bootstrap';

import { useRestApi } from 'context/RestApiContext';
import { ToastMessage } from 'components/modal/ToastMessage';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { toFloat, toInt } from 'utils/numberUtils';
import { getNendoRangeList, getCurrentNendo } from 'utils/dateUtils';
import { useAuth } from 'context/AuthContext';

import './KoujiDaicho.css';

function KoujiRiekiModal(props) {
  const auth = useAuth();
  // 管理者かどうか
  const isAdmin = auth.auth.kbn && auth.auth.kbn.some((k) => ['admin'].includes(k));
  // REST
  const restApi = useRestApi();
  // 年度の範囲
  const nendoRangeList = getNendoRangeList();

  // トースト表示
  const [toast, setToast] = useState({});

  // 工事利益明細
  const [koujiRiekiDetails, setKoujiRiekiDetails] = useState(new CollectionView([]));

  const flexGridRef = useRef(null);
  const totalGridRef = useRef(null);

  // 合計
  const [total, setTotal] = useState([]);

  // 選択年度
  const [selectedYear, setSelectedYear] = useState(0);

  // モーダルの表示
  const [show, setShow] = useState(false);

  // 検索条件を年度に変換
  const toYear = (searchCondition) => {
    // 開始年が指定なしの場合は今年度
    if (!searchCondition.YEAR) {
      const td = new Date();
      if (td.getMonth() === 11) {
        return getCurrentNendo() - 1; // 12月の場合は前年度
      }
      return getCurrentNendo();
    }

    // 開始年の年度を返却(12月でない場合は前年を返す)
    return toInt(searchCondition.MONTH) === 12 ? searchCondition.YEAR : searchCondition.YEAR - 1;
  };

  // 初期化
  useEffect(() => {
    if (props.params.show) {
      const year = toYear(props.params.searchCondition);
      setSelectedYear(year);
      setShow(true);
    }
  }, [props.params]);

  // 年度変更時の処理
  useEffect(() => {
    restApi.get(`/api/order/rieki/${selectedYear}`, (data) => {
      let keihiList = data.keihiList.map((x) => toRieki(x));
      setKoujiRiekiDetails(new CollectionView(keihiList));
      calcTotal(
        keihiList.filter((x) => x.checked),
        data.rieki
      );
    });
  }, [selectedYear]);

  // 工事利益明細の変換処理
  const toRieki = (riekiModel) => {
    let seikyu = toFloat(riekiModel.SEIKSAN_KIN);
    let arari = toFloat(seikyu - riekiModel.CYU_KIN_TOTAL);
    let rieki = toFloat(
      riekiModel.SEIKSAN_KIN - riekiModel.CYU_KIN_TOTAL - (riekiModel.KEIHI ?? 0)
    );
    let riekiritsu = seikyu ? rieki / seikyu : 0;

    return {
      ...riekiModel,
      checked: riekiModel.COUNT_FLG === 1, // COUNT_FLGが1ならチェック済み
      ARARI: arari,
      RIEKI: rieki,
      RIEKI_RITU: !isNaN(riekiritsu) ? riekiritsu : 0,
    };
  };

  // 合計の計算処理
  const calcTotal = (details, riekiInfo) => {
    // 更新前の合計
    const beforeTotal = totalGridRef?.current?.props?.itemsSource ?? [{}, {}];

    // チェックがtrueの明細のみ集計
    const filtered = details.filter((x) => x.checked);
    // 売り上げ額
    let uriage = filtered.reduce((a, x) => a + toFloat(x.SEIKSAN_KIN), 0);
    // 工事原価
    let genka = filtered.reduce((a, x) => a + toFloat(x.CYU_KIN_TOTAL), 0);
    // 棚卸高(期首)
    let tanaStart = riekiInfo
      ? riekiInfo.TANA_START
      : (beforeTotal[0] && beforeTotal[0].TANA_START) || null;
    // 棚卸高(期首)（税抜き）
    let tanaStartZeiNuki = riekiInfo
      ? riekiInfo.TANA_START_ZEINUKI
      : (beforeTotal[1] && beforeTotal[1].TANA_START) || null;
    // 棚卸高(期末)
    let tanaEnd = riekiInfo
      ? riekiInfo.TANA_END
      : (beforeTotal[0] && beforeTotal[0].TANA_END) || null;
    // 棚卸高(期末)（税抜き）
    let tanaEndZeiNuki = riekiInfo
      ? riekiInfo.TANA_END_ZEINUKI
      : (beforeTotal[1] && beforeTotal[1].TANA_END) || null;

    // 決算修正
    let kessan = riekiInfo ? riekiInfo.KESSAN : (beforeTotal[0] && beforeTotal[0].KESSAN) || null;
    // 決算修正（税抜き）
    let kessanZeiNuki = riekiInfo
      ? riekiInfo.KESSAN_ZEINUKI
      : (beforeTotal[1] && beforeTotal[1].KESSAN) || null;
    // 一般販売経費
    let keihi = filtered.reduce((a, x) => a + toFloat(x.KEIHI ?? 0), 0);
    // 一般販売経費(税抜き)
    let keihiZeiNuki = riekiInfo
      ? riekiInfo.KEIHI_ZEINUKI
      : (beforeTotal[1] && beforeTotal[1].KEIHI) || null;

    // 粗利
    let arari = uriage - genka - (tanaStart ?? 0) - (tanaEnd ?? 0);
    // 営業利益額
    let rieki = toFloat(arari - keihi + (kessan ?? 0), 0);
    // 概算法人税(営業利益額 × 30%)
    let houjin_zei = Math.max(toFloat(rieki * 0.3, 0), 0);
    // 概算営業利益額(税引後)(営業利益額 - 概算法人税)
    let zeibiki_rieki = toFloat(rieki - houjin_zei, 0);
    // 概算消費税(粗利 × 10%)
    let zei = toFloat(arari * 0.1, 0);

    // 利益率
    let riekiritsu = uriage ? rieki / uriage : 0;

    // [税抜]
    // 売り上げ金額（税抜）
    let uriageZeiNuki = riekiInfo
      ? riekiInfo.URIAGE_ZEINUKI
      : (beforeTotal[1] && beforeTotal[1].URIAGE) || null;

    // 工事原価（税抜き）
    let genkaZeiNuki = riekiInfo
      ? riekiInfo.GENKA_ZEINUKI
      : (beforeTotal[1] && beforeTotal[1].genka) || null;

    // 粗利（税抜き）
    let arariZeiNuki =
      (uriageZeiNuki ?? 0) - genkaZeiNuki - (tanaStartZeiNuki ?? 0) - (tanaEndZeiNuki ?? 0);
    // 営業利益（税抜き）
    let riekiZeiNuki = arariZeiNuki - (keihiZeiNuki ?? 0) + (kessanZeiNuki ?? 0);

    // 利益（税抜き）
    let riekiritsuZeiNuki = uriageZeiNuki ?? 0 ? riekiZeiNuki / (uriageZeiNuki ?? 0) : 0;

    // 集計結果を反映
    setTotal([
      {
        totalKind: '税込',
        URIAGE: uriage,
        genka: genka,
        TANA_START: tanaStart,
        TANA_END: tanaEnd,
        arari: arari,
        KESSAN: kessan,
        keihi: keihi,
        rieki: rieki,
        riekiritsu: !isNaN(riekiritsu) ? riekiritsu : 0,
        houjin_zei: houjin_zei,
        zeibiki_rieki: zeibiki_rieki,
        zei: zei,
      },
      {
        totalKind: '税抜',
        URIAGE: uriageZeiNuki,
        genka: genkaZeiNuki,
        TANA_START: tanaStartZeiNuki,
        TANA_END: tanaEndZeiNuki,
        arari: arariZeiNuki,
        KESSAN: kessanZeiNuki,
        keihi: keihiZeiNuki,
        rieki: riekiZeiNuki,
        riekiritsu: !isNaN(riekiritsuZeiNuki) ? riekiritsuZeiNuki : 0,
        houjin_zei: null,
        zeibiki_rieki: null,
        zei: null,
      },
    ]);
  };

  // 貼り付け後の処理
  const pasted = (grid, args) => {
    cellEditEnded(grid, args);
  };
  // 編集終了処理
  const cellEditEnded = (grid, args) => {
    console.log('cellEditEnded', args);
    var dataItem = grid.rows[args.row].dataItem;
    var col = grid.columns[args.col];
    let upd = false;

    // チェックボックス変更時
    if (col['binding'] === 'checked') {
      dataItem['checked'] = !!dataItem['checked'];
      upd = true;
    }
    // 経費、売り上げ、工事原価の場合
    if (['KEIHI', 'SEIKSAN_KIN', 'CYU_KIN_TOTAL'].includes(col['binding'])) {
      let keihi = toFloat(dataItem['KEIHI']);
      let seikyu = toFloat(dataItem['SEIKSAN_KIN']);
      let chukin = toFloat(dataItem['CYU_KIN_TOTAL']);
      let arari = seikyu - chukin;
      let rieki = arari - keihi;
      let riekiritsu = seikyu ? rieki / seikyu : 0;
      dataItem['ARARI'] = arari;
      dataItem['RIEKI'] = rieki;
      dataItem['RIEKI_RITU'] = !isNaN(riekiritsu) ? riekiritsu : 0;
      upd = true;
    }

    // 一覧の再描画をおこなう
    if (upd) {
      grid.beginUpdate();
      grid.setCellData(args.row, 4, dataItem['ARARI'], true);
      grid.setCellData(args.row, 6, dataItem['RIEKI'], true);
      grid.setCellData(args.row, 7, dataItem['RIEKI_RITU'], true);
      grid.endUpdate();
      // 合計を計算（チェックがtrueのみ）
      calcTotal(grid.rows.map((x) => x.dataItem));
    }
  };

  // 合計グリッドの貼り付け後の処理
  const totalGridPasted = (grid, args) => {
    totalGridCellEditEnded(grid, args);
  };
  // 合計グリッドの編集終了処理
  const totalGridCellEditEnded = (grid, args) => {
    var col = grid.columns[args.col];
    var row = args.row;

    if (['URIAGE', 'TANA_START', 'TANA_END', 'KESSAN', 'keihi'].includes(col['binding'])) {
      const dataItem = grid.rows[row].dataItem;
      let uriage = toFloat(dataItem['URIAGE']);
      let genka = toFloat(dataItem['genka']);
      let keihi = toFloat(dataItem['keihi']);
      let tanaStart = toFloat(dataItem['TANA_START']);
      let tanaEnd = toFloat(dataItem['TANA_END']);
      let kessan = toFloat(dataItem['KESSAN']);

      let arari = uriage - genka - tanaStart - tanaEnd;
      let rieki = arari - keihi + kessan;
      let riekiritsu = uriage ? rieki / uriage : 0;

      dataItem['arari'] = arari;
      dataItem['rieki'] = rieki;
      dataItem['riekiritsu'] = !isNaN(riekiritsu) ? riekiritsu : 0;

      // 税込みの場合のみ法人税、営業利益額(税引後)、概算消費税を計算
      if (row === 0) {
        // 概算法人税(営業利益額 × 30%)
        let houjin_zei = Math.max(toFloat(rieki * 0.3, 0), 0);
        // 概算営業利益額(税引後)(営業利益額 - 概算法人税)
        let zeibiki_rieki = toFloat(rieki - houjin_zei, 0);
        // 概算消費税(粗利 × 10%)
        let zei = toFloat(arari * 0.1, 0);
        dataItem['houjin_zei'] = houjin_zei;
        dataItem['zeibiki_rieki'] = zeibiki_rieki;
        dataItem['zei'] = zei;
      }

      grid.beginUpdate();

      grid.setCellData(row, 5, dataItem['arari'], true);
      grid.setCellData(row, 8, dataItem['rieki'], true);
      grid.setCellData(row, 9, dataItem['riekiritsu'], true);

      // 税込みの場合のみ法人税、営業利益額(税引後)、概算消費税を設定
      if (row === 0) {
        grid.setCellData(row, 10, dataItem['houjin_zei'], true);
        grid.setCellData(row, 11, dataItem['zeibiki_rieki'], true);
        grid.setCellData(row, 12, dataItem['zei'], true);
      }
      grid.endUpdate();
    }
  };

  // 貼り付け処理（編集開始処理をおこなう）
  const totalGridPastingCell = (s, args) => {
    totalGridBeginningEdit(s, args);
  };
  // 編集開始処理
  const totalGridBeginningEdit = (grid, args) => {
    if (args.panel.cellType == CellType.Cell) {
      var col = grid.columns[args.col];
      var dataItem = grid.rows[args.row].dataItem;

      // 税込みは売上と原価と一般経費は入力不可
      if (['URIAGE', 'genka', 'keihi'].includes(col.binding) && dataItem.totalKind === '税込') {
        args.cancel = true;
      }
    }
  };
  // 合計グリッドのフォーマット処理
  const totalGridFormatItem = (grid, e) => {
    if (e.panel.cellType == CellType.Cell) {
      var col = grid.columns[e.col];
      var dataItem = grid.rows[e.row].dataItem;

      // 売り上げ額と原価と一般経費の背景色を設定
      if (['URIAGE', 'genka', 'keihi'].includes(col.binding)) {
        // 税込きは入力不可
        e.cell.style.background = dataItem.totalKind === '税込' ? 'rgb(236, 219, 219)' : '';
      }
    }
  };

  // 閉じるボタン
  const handleClose = useCallback(() => {
    setShow(false);
    setSelectedYear(0);
    setKoujiRiekiDetails([]);
    props.callback();
  }, [props.params]);

  // 保存ボタン押下
  const handleOnClickSave = () => {
    const keihiList = koujiRiekiDetails.items.map((x) => ({
      ...x,
    }));
    restApi.post(
      `/api/order/rieki/save`,
      {
        keihiList: keihiList.map((x) => ({
          ...x,
          COUNT_FLG: x.checked ? 1 : 0, // カウント対象フラグ
        })),
        rieki: {
          CYU_YEAR: selectedYear,
          TANA_START: total[0]?.TANA_START || null,
          TANA_END: total[0]?.TANA_END || null,
          KESSAN: total[0]?.KESSAN || null,
          URIAGE_ZEINUKI: total[1]?.URIAGE || null,
          GENKA_ZEINUKI: total[1]?.genka || null,
          TANA_START_ZEINUKI: total[1]?.TANA_START || null,
          TANA_END_ZEINUKI: total[1]?.TANA_END || null,
          KESSAN_ZEINUKI: total[1]?.KESSAN || null,
          KEIHI_ZEINUKI: total[1]?.keihi || null,
        },
      },
      (data) => {
        setToast({ type: 'info', message: '保存しました。' });
      }
    );
  };

  return (
    <>
      <Dialog
        fullWidth={true}
        open={show}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            '& .MuiPaper-root': {
              maxWidth: 'calc(100vw - 160px)',
              height: 'calc(100vh - 65px)',
              marginTop: '20px',
            },
          },
        }}
        disableEnforceFocus={true} // フォーカス制御を無効にする
      >
        <DialogTitle className="p-3">{`利益管理`}</DialogTitle>
        <DialogContent className="p-0">
          <Card className="m-0">
            <Card.Body className="p-2 pl-3">
              <Row>
                <Col md={3} className="mb-2">
                  <label>期間</label>
                  <div style={{ width: '260px' }}>
                    <SelectComponent
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.value)}
                    >
                      {nendoRangeList.map((x) => (
                        <option key={x.value} value={x.value}>
                          {x.label}
                        </option>
                      ))}
                    </SelectComponent>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col
                  id="koujiGrid"
                  className="pt-2 pl-3 table-scroll"
                  style={{ height: 'calc(100vh - 380px)' }}
                >
                  <FlexGrid
                    itemsSource={koujiRiekiDetails}
                    ref={flexGridRef}
                    imeEnabled={true}
                    allowSorting={false}
                    autoGenerateColumns={false}
                    keyActionTab={KeyAction.Cycle}
                    cellEditEnded={cellEditEnded}
                    pasted={pasted}
                    autoRowHeights={true}
                  >
                    <FlexGridColumn
                      binding="checked"
                      header=" "
                      width={40}
                      dataType="Boolean"
                      isRequired={false}
                      isReadOnly={false}
                    />
                    <FlexGridColumn
                      binding="CYU_MONTH"
                      header="年月"
                      width={85}
                      isReadOnly={true}
                      cssClass="cell-readonly"
                    />
                    <FlexGridColumn
                      binding="SEIKSAN_KIN"
                      header="売り上げ額"
                      width={160}
                      dataType="Number"
                      isRequired={false}
                      isReadOnly={!isAdmin}
                    />
                    <FlexGridColumn
                      binding="CYU_KIN_TOTAL"
                      header="工事原価"
                      width={160}
                      dataType="Number"
                      isRequired={false}
                      isReadOnly={!isAdmin}
                    />
                    <FlexGridColumn
                      binding="ARARI"
                      header="粗利益額"
                      width={160}
                      isReadOnly={true}
                      cssClass="cell-readonly"
                    />
                    <FlexGridColumn
                      binding="KEIHI"
                      header="一般販売経費"
                      width={160}
                      dataType="Number"
                      isRequired={false}
                      isReadOnly={!isAdmin}
                    />
                    <FlexGridColumn
                      binding="RIEKI"
                      header="営業利益額"
                      width={172}
                      isReadOnly={true}
                      cssClass="cell-readonly"
                    />
                    <FlexGridColumn
                      binding="RIEKI_RITU"
                      header="営業利益率"
                      format="p1"
                      width={100}
                      isReadOnly={true}
                      cssClass="cell-readonly"
                    ></FlexGridColumn>
                    <FlexGridColumn
                      binding="RIEKI_BIKO"
                      header="備考"
                      width={'*'}
                      isRequired={false}
                      isReadOnly={!isAdmin}
                    />
                  </FlexGrid>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </DialogContent>
        <DialogActions className="p-3" style={{ justifyContent: 'space-between' }}>
          <div id="koujiTotalGrid">
            <FlexGrid
              ref={totalGridRef}
              itemsSource={total}
              headersVisibility={'Column'}
              allowSorting={false}
              imeEnabled={true}
              keyActionTab={KeyAction.Cycle}
              autoGenerateColumns={false}
              formatItem={totalGridFormatItem}
              pastingCell={totalGridPastingCell}
              beginningEdit={totalGridBeginningEdit}
              cellEditEnded={totalGridCellEditEnded}
              pasted={totalGridPasted}
            >
              <FlexGridColumn
                binding="totalKind"
                header="　"
                align="center"
                width={48}
                isReadOnly={true}
                cssClass="cell-readonly"
              />
              <FlexGridColumn
                binding="URIAGE"
                header="売り上げ額"
                format="c0"
                width={118}
                dataType={'Number'}
                isRequired={false}
                isReadOnly={!isAdmin}
              ></FlexGridColumn>
              <FlexGridColumn
                binding="genka"
                header="工事原価"
                format="c0"
                width={118}
                dataType={'Number'}
                isRequired={false}
                isReadOnly={!isAdmin}
              ></FlexGridColumn>
              <FlexGridColumn
                binding="TANA_START"
                header="棚卸高(期首)"
                dataType={'Number'}
                format="c0"
                width={118}
                isRequired={false}
                isReadOnly={!isAdmin}
              ></FlexGridColumn>
              <FlexGridColumn
                binding="TANA_END"
                header="棚卸高(期末)"
                format="c0"
                dataType={'Number'}
                width={118}
                isRequired={false}
                isReadOnly={!isAdmin}
              ></FlexGridColumn>
              <FlexGridColumn
                binding="arari"
                header="粗利益額"
                format="c0"
                width={118}
                isReadOnly={true}
                cssClass="cell-readonly"
              ></FlexGridColumn>
              <FlexGridColumn
                binding="KESSAN"
                header="決算修正"
                format="c0"
                width={118}
                dataType={'Number'}
                isRequired={false}
                isReadOnly={!isAdmin}
              ></FlexGridColumn>
              <FlexGridColumn
                binding="keihi"
                header="一般販売経費"
                format="c0"
                isRequired={false}
                isReadOnly={!isAdmin}
                width={118}
              ></FlexGridColumn>
              <FlexGridColumn
                binding="rieki"
                header="営業利益額"
                format="c0"
                width={118}
                isReadOnly={true}
                cssClass="cell-readonly"
              ></FlexGridColumn>
              <FlexGridColumn
                binding="riekiritsu"
                header="営業利益率"
                format="p1"
                width={92}
                isReadOnly={true}
                cssClass="cell-readonly"
              ></FlexGridColumn>
              <FlexGridColumn
                binding="houjin_zei"
                header="法人税"
                format="c0"
                isReadOnly={true}
                cssClass="cell-readonly"
                width={112}
              ></FlexGridColumn>
              <FlexGridColumn
                binding="zeibiki_rieki"
                header="営業利益額(税引後)"
                format="c0"
                isReadOnly={true}
                cssClass="cell-readonly"
                width={160}
              ></FlexGridColumn>
              <FlexGridColumn
                binding="zei"
                header="概算消費税"
                format="c0"
                isReadOnly={true}
                cssClass="cell-readonly"
                width={112}
              ></FlexGridColumn>
            </FlexGrid>
          </div>
          <div>
            <Button className="footer-button" variant="secondary" onClick={handleClose}>
              閉じる
            </Button>
            {isAdmin && (
              <Button
                className="btn-fill footer-button ml-3"
                variant="primary"
                onClick={handleOnClickSave}
              >
                保存
              </Button>
            )}
          </div>
        </DialogActions>
      </Dialog>
      {/* トーストメッセージ */}
      <ToastMessage toast={toast}></ToastMessage>
    </>
  );
}

export default KoujiRiekiModal;
